import { ChevronDown, Download } from "lucide-react";
import { Link } from "react-router";

import { MoneyValue } from "@/components/common/money-value";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatSnakeLabel } from "@/lib/labels";

import { buildLlmCostCsv } from "./llm-cost-csv";
import type { LlmCostBreakdown, LlmCostBreakdownEvent, LlmCostBreakdownGroup } from "./types";

const formatJobTypeLabel = (jobType: string): string => formatSnakeLabel(jobType);

const formatTokenCount = (value: number | null): string =>
  value === null ? "—" : value.toLocaleString();

const formatUnpricedEventCount = (count: number): string =>
  count === 1 ? "1 unpriced event" : `${count} unpriced events`;

const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

function LlmCostTraceTable({
  events,
  formatDate,
}: {
  events: LlmCostBreakdownEvent[];
  formatDate: (value: string) => string;
}) {
  return (
    <div className="overflow-hidden rounded-xl border bg-background">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>When</TableHead>
            <TableHead>Job / Component</TableHead>
            <TableHead>Model</TableHead>
            <TableHead>Tokens</TableHead>
            <TableHead>Price Used</TableHead>
            <TableHead className="text-right">Cost</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id} className="align-top">
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                {formatDate(event.createdAt)}
              </TableCell>
              <TableCell>
                <div className="font-mono text-xs">{event.jobType}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {event.component ?? "unknown component"}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-xs font-medium">{event.provider}</div>
                <div className="mt-1 font-mono text-xs text-muted-foreground">{event.model}</div>
              </TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                <div>in {formatTokenCount(event.inputTokens)}</div>
                <div>out {formatTokenCount(event.outputTokens)}</div>
                <div>total {formatTokenCount(event.totalTokens)}</div>
              </TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                <div>in ${event.inputPerMillion ?? "—"}/M</div>
                <div>out ${event.outputPerMillion ?? "—"}/M</div>
                <div>{event.priceSource ?? "no price source"}</div>
                {event.priceSourceFetchedAt ? (
                  <div>{formatDate(event.priceSourceFetchedAt)}</div>
                ) : null}
              </TableCell>
              <TableCell className="text-right font-mono text-xs tabular-nums">
                {event.estimatedCostUsd === null ? (
                  <span className="font-medium text-amber-700">Unknown</span>
                ) : (
                  <MoneyValue amount={event.estimatedCostUsd} precision={6} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function LlmCostGroupCard({
  brandId,
  group,
  events,
  formatDate,
}: {
  brandId: string;
  group: LlmCostBreakdownGroup;
  events: LlmCostBreakdownEvent[];
  formatDate: (value: string) => string;
}) {
  const areaTone =
    group.area === "onboarding"
      ? "border-blue-200 bg-blue-50 text-blue-700"
      : group.area === "campaign"
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <div className="rounded-2xl border bg-background p-4 shadow-xs">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={areaTone}>
              {group.area}
            </Badge>
            {group.campaignId ? (
              <Link
                to={`/brands/${brandId}/campaigns/${group.campaignId}`}
                className="truncate text-base font-semibold hover:underline"
              >
                {group.label}
              </Link>
            ) : (
              <h3 className="truncate text-base font-semibold">{group.label}</h3>
            )}
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span>
              {group.eventCount} usage event{group.eventCount === 1 ? "" : "s"}
            </span>
            <span>/</span>
            <span>
              {group.models.length} model{group.models.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-xl font-semibold tabular-nums">
            <MoneyValue amount={group.totalCostUsd} precision={6} />
          </div>
          {group.unpricedEventCount > 0 ? (
            <div className="text-xs text-muted-foreground">
              {formatUnpricedEventCount(group.unpricedEventCount)}
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-4 grid gap-3 xl:grid-cols-2">
        <div className="rounded-xl border bg-muted/20 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Job types
          </div>
          <div className="space-y-2">
            {group.jobTypes.map((item) => (
              <div key={item.jobType} className="flex items-center justify-between gap-3 text-sm">
                <span
                  className="truncate font-mono text-xs"
                  title={formatJobTypeLabel(item.jobType)}
                >
                  {item.jobType}
                </span>
                <div className="text-right">
                  <div className="font-mono tabular-nums text-muted-foreground">
                    <MoneyValue amount={item.totalCostUsd} precision={6} />
                  </div>
                  {item.unpricedEventCount > 0 ? (
                    <div className="text-[11px] text-muted-foreground">
                      {formatUnpricedEventCount(item.unpricedEventCount)}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border bg-muted/20 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Models
          </div>
          <div className="space-y-2">
            {group.models.map((item) => (
              <div
                key={`${item.provider}:${item.model}`}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span className="truncate font-mono text-xs">
                  {item.provider}/{item.model}
                </span>
                <div className="text-right">
                  <div className="font-mono tabular-nums text-muted-foreground">
                    <MoneyValue amount={item.totalCostUsd} precision={6} />
                  </div>
                  {item.unpricedEventCount > 0 ? (
                    <div className="text-[11px] text-muted-foreground">
                      {formatUnpricedEventCount(item.unpricedEventCount)}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <details className="mt-4 group/trace">
        <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground">
          <ChevronDown className="size-4 transition-transform group-open/trace:rotate-180" />
          Price and model trace
        </summary>
        <div className="mt-3">
          <LlmCostTraceTable events={events} formatDate={formatDate} />
        </div>
      </details>
    </div>
  );
}

export function LlmCostBreakdownCard({
  brandId,
  breakdown,
  isPending,
  formatDate,
}: {
  brandId: string;
  breakdown: LlmCostBreakdown | undefined;
  isPending: boolean;
  formatDate: (value: string) => string;
}) {
  const handleDownload = () => {
    if (!breakdown || breakdown.events.length === 0) return;
    downloadCsv(`llm-cost-${brandId}.csv`, buildLlmCostCsv(breakdown.events));
  };

  const totalCost = breakdown?.totalCostUsd ?? 0;

  return (
    <Card className="h-fit overflow-hidden">
      <CardHeader className="border-b bg-muted/20">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle className="text-sm font-semibold">LLM Cost</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Estimated spend grouped into onboarding and campaign work, with price traceability.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={!breakdown || breakdown.events.length === 0}
            >
              <Download className="mr-2 size-4" />
              Download CSV
            </Button>
            <div className="rounded-2xl border bg-background px-3 py-2 text-right shadow-xs">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Total
              </div>
              <div className="font-mono text-lg font-semibold tabular-nums">
                <MoneyValue amount={totalCost} precision={6} />
              </div>
              {breakdown && breakdown.unpricedEventCount > 0 ? (
                <div className="text-[11px] text-muted-foreground">
                  {formatUnpricedEventCount(breakdown.unpricedEventCount)}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-28 w-full rounded-2xl" />
            <Skeleton className="h-28 w-full rounded-2xl" />
          </div>
        ) : breakdown && breakdown.groups.length > 0 ? (
          <div className="space-y-3">
            {breakdown.groups.map((group) => (
              <LlmCostGroupCard
                key={group.key}
                brandId={brandId}
                group={group}
                formatDate={formatDate}
                events={breakdown.events.filter((event) => {
                  if (group.area === "onboarding") return event.area === "onboarding";
                  if (group.area === "campaign") {
                    return (
                      (event.campaignId ?? "unattributed") === (group.campaignId ?? "unattributed")
                    );
                  }
                  return event.area === group.area && event.jobType === group.label;
                })}
              />
            ))}
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
            No usage events recorded for this brand.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
